import React, {FC, useCallback, useRef, useState} from 'react';
import classnames from 'classnames';
import {BlueWhiteLogoHeader, LoadingSpinner, LogoutIcon} from 'components';
import {providerRoles} from 'constants/roles';
import {ROUTES} from 'constants/routes';
import {selectUnreadNotificationsCountByType} from 'features/Notification';
import {isProviderProfilePage} from 'features/Provider/Profile/utils';
import {selectUserProfile} from 'features/User';
import {useLogout, useUserNotifications, useWindowSize} from 'hooks';
import {SliceStatus, UserRoles} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router';
import styled from 'styled-components';
import {isUserFromUnitedStates, toCamelCase} from 'utils';

import {WellbitsRewardNotification} from './Collaboration';
import {filterNavMenuItems, NavMenuItem} from './navigationConstants';
import {NavLinkV2} from './NavLinkV2';

const NavigationContainer = styled.section`
  height: calc(100% - 5rem);
`;
export const LeftNavigationV2: FC = () => {
  const location = useLocation();
  const {pathname} = location;
  const user = useSelector(selectUserProfile);
  const wellbitsNotificationCount = useSelector(
    selectUnreadNotificationsCountByType('wellbits_created'),
  );
  const {t} = useTranslation();
  const {width} = useWindowSize();

  const collaborationMenuRef = useRef<HTMLDivElement>(null);
  const [expandedParent, setExpandedParent] = useState<string[]>(['Revenue']);

  const {logout, logoutStatus} = useLogout(true);

  const userRole = user?.role;
  const isUserFromUS = isUserFromUnitedStates(user);
  const filteredNavMenuItems = filterNavMenuItems(userRole!, isUserFromUS);
  const {unreadNotifsLength} = useUserNotifications();

  const isMobileMenu = Boolean(width && width < 1024);
  const handleParentClick = useCallback((name: string) => {
    setExpandedParent(prev =>
      prev.includes(name)
        ? prev.filter(item => item !== name)
        : [...prev, name],
    );
  }, []);
  // Helper to check if a route or any of its children is active
  const isActive = (
    route?: string,
    routeRegex?: RegExp,
    children?: NavMenuItem[],
  ): boolean => {
    if (route && (pathname === route || routeRegex?.test(pathname))) {
      return true;
    }
    if (children) {
      return children.some(child =>
        isActive(child.route, child.routeRegex, child.children),
      );
    }
    return false;
  };

  // Recursive render function that preserves custom logic for badges and notifications
  const renderNavItems = (
    items: NavMenuItem[],
    isChild = false,
  ): React.ReactNode[] =>
    items.map(
      ({name, route, icon: Icon, iconClasses, routeRegex, children}) => {
        const hasChildren = Array.isArray(children) && children.length > 0;
        const active = isActive(route, routeRegex, children);
        if (hasChildren) {
          const expanded = expandedParent.includes(name);
          return (
            <div key={name}>
              <NavLinkV2
                isParent
                linkName={t(`navigationName.${toCamelCase(name)}`)}
                current={active}
                onClick={() => handleParentClick(name)}
                classNames={isChild ? 'pl-8' : ''}
                pathname={route}
              >
                {Icon ? <Icon classes={iconClasses} /> : null}
              </NavLinkV2>
              {expanded ? (
                <div
                  className={classnames('flex flex-col gap-4 ml-4', {
                    'my-4': active,
                  })}
                >
                  {renderNavItems(children, true)}
                </div>
              ) : null}
            </div>
          );
        }
        // Leaf node: preserve original logic for badges and notifications
        return (
          <div key={route}>
            <NavLinkV2
              key={route}
              pathname={route}
              current={
                pathname === route ||
                (!!routeRegex && routeRegex.test(pathname))
              }
              linkName={t(`navigationName.${toCamelCase(name)}`)}
              isChild={isChild}
              classNames={isChild ? 'pl-8' : ''}
            >
              <div className="relative" ref={collaborationMenuRef}>
                {!!Icon && (
                  <Icon
                    strokeColor={pathname === route ? 'text-white' : ''}
                    classes={iconClasses}
                  />
                )}

                {route === ROUTES.NOTIFICATIONS.LIST && unreadNotifsLength ? (
                  <span
                    className={classnames(
                      'absolute -top-2 right-0 inline-flex items-center rounded-full px-1.5 h-4 text-[10px] font-semibold text-blue-600 ring-1 ring-inset ring-blue-600/50',
                      {
                        hidden: pathname === route,
                        'bg-blue-600/[15%]': pathname !== route,
                      },
                    )}
                  >
                    {unreadNotifsLength}
                  </span>
                ) : null}

                {route === ROUTES.PROVIDER.DIGITAL_PRACTICE.EARNINGS &&
                wellbitsNotificationCount ? (
                  <>
                    <span
                      className={classnames(
                        'absolute -top-2.5 -right-3 inline-flex items-center justify-center rounded-full w-5 h-5 text-[10px] font-semibold text-white bg-red-500',
                        {
                          hidden: pathname === route,
                        },
                      )}
                    >
                      {wellbitsNotificationCount}
                    </span>
                    {isMobileMenu ? null : (
                      <WellbitsRewardNotification
                        ref={collaborationMenuRef}
                        onClose={() => {}}
                      />
                    )}
                  </>
                ) : null}
              </div>
            </NavLinkV2>
          </div>
        );
      },
    );

  return (
    <>
      <BlueWhiteLogoHeader />
      <NavigationContainer className="pt-6 w-full flex flex-col lg:justify-center lg:items-center xl:items-start">
        <nav className="w-full overflow-y-auto">
          {userRole ? renderNavItems(filteredNavMenuItems) : null}
          {userRole == UserRoles.member ? (
            <NavLinkV2
              pathname={ROUTES.MEMBER.ACCOUNT}
              current={[
                ROUTES.MEMBER.PROFILE,
                ROUTES.MEMBER.MEMBERSHIP,
                ROUTES.TERMS,
                ROUTES.MEMBER.PAYMENT,
              ].includes(pathname)}
              linkName={t('navigationName.profile', 'Profile')}
            >
              <img
                src={user?.image}
                className="w-6 h-6 bg-gray-300 rounded-full object-cover"
                alt="profile-photo"
              />
            </NavLinkV2>
          ) : null}
          {providerRoles.includes((userRole ?? '') as unknown as UserRoles) ? (
            <NavLinkV2
              pathname={ROUTES.PROVIDER.PROFILE}
              current={isProviderProfilePage(pathname)}
              linkName={t('navigationName.profile', 'Profile')}
            >
              <img
                src={user?.image}
                className="w-6 h-6 bg-gray-300 rounded-full"
                alt="profile-photo"
              />
            </NavLinkV2>
          ) : null}
        </nav>
        <button
          onClick={logout}
          className={classnames(
            'flex items-center gap-5 text-red-500 px-5 py-6 text-lg w-full leading-snug mt-auto cursor-pointer',
            {
              'pointer-events-none opacity-50':
                logoutStatus === SliceStatus.pending,
            },
          )}
        >
          {logoutStatus === SliceStatus.pending ? (
            <LoadingSpinner
              type="Oval"
              width={20}
              height={20}
              color="#EF4444"
            />
          ) : (
            <LogoutIcon
              width={25}
              height={25}
              strokeWidth={25}
              strokeColor="text-red-500"
            />
          )}
          <span className="lg:hidden xl:block">{t('logout')}</span>
        </button>
      </NavigationContainer>
    </>
  );
};
